// The animated service map. Boxes are the parts of the system, lines are the
// traffic between them, dots are that traffic (more dots, more calls). The state
// of a box and of a line comes from the server; this draws it and moves the dots.

import { useEffect, useMemo, useRef, type JSX } from 'react';
import {
  NODE_H,
  NODE_W,
  VIEW_H,
  VIEW_W,
  WORKER_BOX,
  buildMap,
  type MapEdge,
  type MapNode,
  type NodeId,
} from './map-model.js';
import { ParticleField, type DotEdge } from './particles.js';
import type { OpsOverview } from '../../api/ops-types.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

interface Props {
  readonly overview: OpsOverview;
  readonly selected: NodeId | null;
  readonly onSelect: (id: NodeId) => void;
  readonly paused: boolean;
}

export function OpsMap({ overview, selected, onSelect, paused }: Props): JSX.Element {
  const { nodes, edges } = useMemo(() => buildMap(overview), [overview]);
  const dotsRef = useRef<SVGGElement | null>(null);
  const edgesRef = useRef<readonly DotEdge[]>(edges);
  const pausedRef = useRef(paused);
  const field = useRef<ParticleField | null>(null);
  if (!field.current) field.current = new ParticleField();
  edgesRef.current = edges;
  pausedRef.current = paused;

  const reduce =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const g = dotsRef.current;
    const f = field.current;
    if (!g || !f) return undefined;
    const pool: SVGCircleElement[] = [];

    const draw = (): void => {
      const places = f.places(edgesRef.current);
      while (pool.length < places.length) {
        const c = document.createElementNS(SVG_NS, 'circle');
        c.setAttribute('r', '3.2');
        g.appendChild(c);
        pool.push(c);
      }
      while (pool.length > places.length) pool.pop()!.remove();
      places.forEach((p, i) => {
        const c = pool[i]!;
        c.setAttribute('cx', p.x.toFixed(1));
        c.setAttribute('cy', p.y.toFixed(1));
        c.setAttribute('class', `ops-dot ${p.state}`);
        c.setAttribute('opacity', p.opacity.toFixed(2));
      });
    };

    if (reduce) {
      f.place(edgesRef.current);
      draw();
      return () => {
        for (const c of pool) c.remove();
      };
    }

    f.prefill(edgesRef.current);
    let raf = 0;
    let last = 0;
    const frame = (ts: number): void => {
      if (last === 0) last = ts;
      const dt = Math.min(0.1, (ts - last) / 1000);
      last = ts;
      if (!pausedRef.current) {
        f.step(edgesRef.current, dt);
        draw();
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      for (const c of pool) c.remove();
    };
  }, [reduce]);

  // Reduced motion draws fixed dots: redo them when the lines change.
  useEffect(() => {
    if (!reduce) return;
    const g = dotsRef.current;
    const f = field.current;
    if (!g || !f) return;
    f.place(edges);
    g.replaceChildren();
    for (const p of f.places(edges)) {
      const c = document.createElementNS(SVG_NS, 'circle');
      c.setAttribute('r', '3.2');
      c.setAttribute('cx', p.x.toFixed(1));
      c.setAttribute('cy', p.y.toFixed(1));
      c.setAttribute('class', `ops-dot ${p.state}`);
      g.appendChild(c);
    }
  }, [reduce, edges]);

  const worker = nodes.find((n) => n.id === 'worker')!;
  const inner = nodes.filter((n) => n.id !== 'worker');

  return (
    <div className="ops-map-scroll">
      <svg
        className={`ops-map${selected ? ' has-sel' : ''}`}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="group"
        aria-label="Services and the traffic between them"
      >
        <g>
          {edges.map((e) => (
            <EdgeLine key={e.id} edge={e} />
          ))}
        </g>
        <g ref={dotsRef} aria-hidden="true" />
        <WorkerBox node={worker} selected={selected === 'worker'} onSelect={onSelect} />
        <g>
          {inner.map((n) => (
            <NodeBox key={n.id} node={n} selected={selected === n.id} onSelect={onSelect} />
          ))}
        </g>
      </svg>
    </div>
  );
}

function EdgeLine({ edge }: { edge: MapEdge }): JSX.Element {
  return (
    <path
      d={edge.path}
      className={`ops-edge ${edge.state}`}
      data-edge={edge.id}
      data-state={edge.state}
    />
  );
}

function activate(
  node: MapNode,
  onSelect: (id: NodeId) => void,
): {
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
} {
  return {
    onClick: () => onSelect(node.id),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(node.id);
      }
    },
  };
}

function NodeBox({
  node,
  selected,
  onSelect,
}: {
  node: MapNode;
  selected: boolean;
  onSelect: (id: NodeId) => void;
}): JSX.Element {
  const interactive = node.measured;
  const cls = `ops-node st-${node.status}${node.measured ? '' : ' unmeasured'}${selected ? ' sel' : ''}`;
  return (
    <g
      className={cls}
      transform={`translate(${node.x},${node.y})`}
      data-node={node.id}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `${node.name}, ${node.metric}. ${node.sub}. Press to open.`,
            ...activate(node, onSelect),
          }
        : { 'aria-label': `${node.name}, ${node.metric}` })}
    >
      <rect className="nbox" width={NODE_W} height={NODE_H} rx={10} />
      {node.measured ? <circle className="sdot" cx={14} cy={16} r={4} /> : null}
      <text className="nname" x={node.measured ? 26 : 12} y={20}>
        {node.name}
      </text>
      <text className="nsub" x={12} y={37}>
        {node.sub}
      </text>
      <text className="nmet" x={12} y={55}>
        {node.metric}
      </text>
    </g>
  );
}

function WorkerBox({
  node,
  selected,
  onSelect,
}: {
  node: MapNode;
  selected: boolean;
  onSelect: (id: NodeId) => void;
}): JSX.Element {
  return (
    <g
      className={`ops-node ops-worker st-${node.status}${selected ? ' sel' : ''}`}
      data-node="worker"
      role="button"
      tabIndex={0}
      aria-label={`Worker, ${node.sub}. Press to open.`}
      {...activate(node, onSelect)}
    >
      <rect
        className="nbox"
        x={WORKER_BOX.x}
        y={WORKER_BOX.y}
        width={WORKER_BOX.w}
        height={WORKER_BOX.h}
        rx={12}
      />
      <circle className="sdot" cx={WORKER_BOX.x + 16} cy={WORKER_BOX.y + 20} r={4} />
      <text className="nname" x={WORKER_BOX.x + 28} y={WORKER_BOX.y + 24}>
        Worker
      </text>
      <text className="nsub" x={WORKER_BOX.x + 80} y={WORKER_BOX.y + 24}>
        {node.sub.replace(' machines running', ' up')}
      </text>
    </g>
  );
}
